import * as React from "react";
import { Field } from "react-final-form";
import { FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";

/**
 * Generates period options from 2024-1 to the current month.
 * Each period label is "YYYY-M" and its value is the ISO date string
 * of the first day of that month (e.g. "2024-01-01").
 */
const generatePeriods = (): { label: string; value: string }[] => {
  const periods: { label: string; value: string }[] = [];
  const now = new Date();
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1; // 1-based

  for (let year = 2024; year <= endYear; year++) {
    const maxMonth = year === endYear ? endMonth : 12;
    for (let month = 1; month <= maxMonth; month++) {
      const label = `${year}-${month}`;
      const mm = String(month).padStart(2, "0");
      const value = `${year}-${mm}-01`;
      periods.push({ label, value });
    }
  }

  return periods;
};

const periods = generatePeriods();

interface PeriodSelectProps {
  source: string;
  label?: string;
  fullWidth?: boolean;
  variant?: "outlined" | "filled" | "standard";
  defaultValue?: string;
}

/**
 * PeriodSelect — a dropdown that lists periods from 2024-1 to the current month.
 * When a period is selected its value is the ISO date string of the first day
 * of that month (e.g. selecting "2024-1" yields "2024-01-01").
 *
 * Integrates with react-final-form via the Field component, so it works
 * inside a <Form> just like other react-admin inputs.
 */
export const PeriodSelect: React.FC<PeriodSelectProps> = ({
  source,
  label = "Period",
  fullWidth = false,
  variant = "outlined",
  defaultValue = "",
}) => {
  return (
    <Field name={source} defaultValue={defaultValue}>
      {({ input }) => (
        <FormControl fullWidth={fullWidth} variant={variant} style={{ marginTop: 8, marginBottom: 4 }}>
          <InputLabel>{label}</InputLabel>
          <Select value={input.value || ""} onChange={e => input.onChange(e.target.value)} label={label}>
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {periods.map(period => (
              <MenuItem key={period.label} value={period.value}>
                {period.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Field>
  );
};

export default PeriodSelect;
